import React, { useContext, useState, useEffect } from 'react'
import { 
    CardNumberElement, 
    CardCvcElement, 
    CardExpiryElement, 
    useStripe, 
    useElements 
} from '@stripe/react-stripe-js'
import { useNavigate } from 'react-router-dom'

import stripeHelpers from '../../helpers/stripe'
import StripeSecretContext from '../../context/StripeSecret'
import SelectedPlanContext from '../../context/SelectedPlan'
import ToastContext from '../../context/Toast'
import { returnUrl, env } from '../../env'
import TestPaymentInfo from '../TestPaymentInfo'
import validator from '../../helpers/validator'
import LoadingWheelWhite from '../LoadingWheelWhite'

import './PaymentForm.scss'

/**
 * Custom payment form integrated with stripe elements (specifically, the CardNumberElement, CardExpiryElement, and CardCvcElement components from react-stripe-js)
 *
 * @component
 * @example
 * return (
 *   <PaymentForm />
 * )
*/

const PaymentForm = () => {

    const navigator = useNavigate()
    
    const stripe = useStripe()
    const elements = useElements()

    const { stripeSecret, setStripeSecret } = useContext(StripeSecretContext)
    const { selectedPlan } = useContext(SelectedPlanContext)
    const { setToast } = useContext(ToastContext)

    const [submitting, setSubmitting] = useState(false)
    const [processingPayment, setProcessingPayment] = useState(false)
    const [formData, setFormData] = useState({ email: '', firstName: '', lastName: ''})

    const validateFormData = ({ email, firstName, lastName }) => {

        if (!firstName) {
            setToast('First name is required! Must be at least two characters long.')
            return false
        }

        if (!lastName) {
            setToast('Last name is required! Must be at least two characters long.')
            return false
        }

        if (!validator.validateEmail(email)) {
            setToast('Email must be a valid email address.')
            return false
        }

        return true

    }

    const handleConfirmPayment = async evt => {

        setSubmitting(true)

        evt.preventDefault()

        if (!stripe || !elements) {
            setToast('There was a problem with our payment system. Please refresh the page and try again.')
            setSubmitting(false)
            return
        }

        const formIsValid = validateFormData(formData)

        if (formIsValid) {

            // must generate new client secret with new product, etc
            const secretRes = await stripeHelpers.fetchClientSecret(formData.email, selectedPlan.name)

            setStripeSecret(secretRes.clientSecret)
            setProcessingPayment(true);

        } else setSubmitting(false);
    }

    const processPayment = async () => {

        if (processingPayment && stripeSecret) {
    
            // actually process payment with stripe
            const paymentOptions = {
                payment_method: {
                    card: elements.getElement(CardNumberElement),
                    billing_details: {
                        name: `${formData.firstName} ${formData.lastName}`,
                        email: formData.email
                    }
                },
                return_url: returnUrl,
                receipt_email: formData.email
            }

            console.log('paymentOptions before submission')
            console.log(paymentOptions)
    
            const paymentRes = await stripe.confirmCardPayment(stripeSecret, paymentOptions)
    
            if (paymentRes.error) setToast('Payment failed! Please try again.')
            else if (paymentRes.paymentIntent.status === 'succeeded') navigator(`/thanks?purchase_completed=true&plan_purchased=${selectedPlan.name}`)
            
            setSubmitting(false)
            setProcessingPayment(false)
        }

    }

    const handleInputChange = (evt, fieldName) => setFormData({ ...formData, [fieldName]: evt.target.value })

    const elementOptions = {
        style: {
            base: {
                iconColor: '#c4f0ff',
                color: '#254778',
                fontWeight: 'normal',
                fontFamily: 'Work Sans, Open Sans, Segoe UI, sans-serif',
                fontSize: '16px',
                fontSmoothing: 'antialiased',
                ':-webkit-autofill': {
                    color: '#254778',
                },
                '::placeholder': {
                    color: '##6c757d',
                },
            },
            invalid: {
                iconColor: '#FFC7EE',
                color: '#FFC7EE',
            },
        }
    }

    useEffect(() => processPayment(), [stripeSecret, processingPayment])
    
    return (
        <form className="row gx-3 py-2" id="checkoutForm" onSubmit={handleConfirmPayment}>

            <h5 className="form-title py-2">Billing Information</h5>

            <div className="col-md-6 formInputGroup">
                <label htmlFor="firstName" className="form-label text-nowrap">First Name</label>
                <input type="text" className="form-control" id="firstName" onChange={evt => handleInputChange(evt, 'firstName')} value={formData.firstName} placeholder="John" />
            </div>

            <div className="col-md-6 formInputGroup">
                <label htmlFor="lastName" className="form-label text-nowrap">Last Name</label>
                <input type="text" className="form-control" id="lastName" onChange={evt => handleInputChange(evt, 'lastName')} value={formData.lastName} placeholder="Doe" />
            </div>

            <div className="col-md-6 formInputGroup">
                <label htmlFor="email" className="form-label text-nowrap">Email Address</label>
                <input type="email" className="form-control" id="email" onChange={evt => handleInputChange(evt, 'email')} value={formData.email} placeholder="johndoe@email.com" />
            </div>

            <div className="col-md-6">
                <div className="stripeElementWrapper">
                    <label htmlFor="cardNumber" className="form-label text-nowrap">CC Number</label>
                    <CardNumberElement id="cardNumber" options={elementOptions} />
                </div>
            </div>

            <div className="col-md-6">
                <div className="stripeElementWrapper">
                    <label htmlFor="cardExpiry" className="form-label text-nowrap">Exp. Date</label>
                    <CardExpiryElement id="cardExpiry" options={elementOptions} />
                </div>
            </div>

            <div className="col-md-6">
                <div className="stripeElementWrapper">
                    <label htmlFor="cardCvc" className="form-label text-nowrap">CVC</label>
                    <CardCvcElement id="cardCvc" options={elementOptions} />
                </div>
            </div>

            {env === "local" || env === "staging" ? (
                <div className="col-md-12">
                    <TestPaymentInfo />
                </div>
            ) : null}

            <div className="col-12">
                <button id="submitBtn" type="submit" className="btn btn-primary btn-lg py-2 px-5 text-white" disabled={submitting}>
                    {submitting ? (
                        <div className="d-flex align-items-center justify-content-center">
                            <LoadingWheelWhite />
                            Processing Payment
                        </div>
                    ) : 'Confirm'}
                </button>
            </div>

        </form>
    )

}

export default PaymentForm